import React, { useEffect, useState } from 'react';
import OwnerSidebar from '../../../Components/OwnerSidebar/OwnerSidebar';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './BannerManagementPage.css';
import ownerAPI from '../../../services/ownerApi';

export default function BannerManagementPage() {
  const { owner, isAuthenticated } = useSelector((state) => state.ownerAuth);
  const navigate = useNavigate();
  
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    expires_at: '',
    file: null
  });

  // 권한 체크
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/owner/login');
      return;
    }
    
    if (owner && !owner.is_superuser) {
      navigate('/owner/dashboard');
      return;
    }
  }, [isAuthenticated, owner, navigate]);

  // 배너 목록 조회
  useEffect(() => {
    if (owner?.is_superuser) {
      fetchBanners();
    }
  }, [owner]);

  const fetchBanners = async () => {
    try {
      const banners = await ownerAPI.getBanners();
      setBanners(banners);
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError('배너 목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (banner = null) => {
    setSelectedBanner(banner);
    if (banner) {
      setFormData({
        title: banner.title,
        description: banner.description || '',
        link: banner.link,
        expires_at: banner.expires_at ? banner.expires_at.split('T')[0] : '',
        file: null
      });
    } else {
      setFormData({
        title: '',
        description: '',
        link: '',
        expires_at: '',
        file: null
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedBanner(null);
    setIsModalOpen(false);
    setFormData({
      title: '',
      description: '',
      link: '',
      expires_at: '',
      file: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('ownerToken');
    const formDataToSend = new FormData();
    
    formDataToSend.append('title', formData.title);
    formDataToSend.append('link', formData.link);
    if (formData.description) formDataToSend.append('description', formData.description);
    if (formData.expires_at) formDataToSend.append('expires_at', formData.expires_at);
    if (formData.file) formDataToSend.append('file', formData.file);

    try {
      if (selectedBanner) {
        // 수정
        await ownerAPI.updateBanner(selectedBanner.id, formDataToSend);
      } else {
        // 생성
        await ownerAPI.createBanner(formDataToSend);
      }
      
      fetchBanners();
      closeModal();
    } catch (err) {
      console.error('Error saving banner:', err);
      setError('배너 저장에 실패했습니다.');
    }
  };

  const handleDelete = async (bannerId) => {
    if (window.confirm('정말로 이 배너를 삭제하시겠습니까?')) {
      try {
        await ownerAPI.deleteBanner(bannerId);
        fetchBanners();
      } catch (err) {
        console.error('Error deleting banner:', err);
        setError('배너 삭제에 실패했습니다.');
      }
    }
  };

  if (!owner?.is_superuser) {
    return null; // 권한이 없으면 아무것도 렌더링하지 않음
  }

  return (
    <div className="banner-management-wrapper">
      <OwnerSidebar />
      <main className={`banner-management-main`}>
        <div className="banner-management-header">
          <h1>배너 관리</h1>
          <button className="add-banner-btn" onClick={() => openModal()}>
            새 배너 추가
          </button>
        </div>

        {error && <p className="error-text">{error}</p>}

        <div className="banner-list-container">
          {loading ? (
            <p>로딩 중...</p>
          ) : (
            <div className="banner-grid">
              {banners.map((banner) => (
                <div key={banner.id} className="banner-card">
                  <div className="banner-image">
                    <img src={banner.image_url} alt={banner.title} />
                  </div>
                  <div className="banner-info">
                    <h3>{banner.title}</h3>
                    <p className="banner-description">{banner.description}</p>
                    <p className="banner-link">
                      <a href={banner.link} target="_blank" rel="noopener noreferrer">
                        {banner.link}
                      </a>
                    </p>
                    {banner.expires_at && (
                      <p className="banner-expires">
                        만료일: {new Date(banner.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="banner-actions">
                    <button className="edit-btn" onClick={() => openModal(banner)}>
                      수정
                    </button>
                    <button className="delete-btn" onClick={() => handleDelete(banner.id)}>
                      삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 모달 */}
        {isModalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedBanner ? '배너 수정' : '새 배너 추가'}</h2>
                <button className="modal-close" onClick={closeModal}>×</button>
              </div>
              
              <form onSubmit={handleSubmit} className="banner-form">
                <div className="form-group">
                  <label>제목 *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>설명</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows="3"
                  />
                </div>
                
                <div className="form-group">
                  <label>링크 URL *</label>
                  <input
                    type="url"
                    value={formData.link}
                    onChange={(e) => setFormData({...formData, link: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>만료일</label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>이미지 {!selectedBanner && '*'}</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                    required={!selectedBanner}
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" onClick={closeModal}>취소</button>
                  <button type="submit">
                    {selectedBanner ? '수정' : '추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
} 